export type PinLockState = {
  hasPin: boolean;
  timeoutSeconds: number;
  locked: boolean;
  lastActivityAt: number;
  settingsCheckedAt?: number;
  settingsUpdatedAt?: string | null;
  settingsGeneration?: number;
};
export type PinSettings = { hasPin: boolean; timeoutSeconds: number; updatedAt?: string | null };
export const PIN_LOCK_CHANGED = 'pos-pin-lock-changed';
export const getPinLockKey = () => {
  let user: any = {};
  try { user = JSON.parse(localStorage.getItem('posUserData') || '{}'); } catch { /* use username fallback */ }
  return `posPinLock:${String(user?.id || user?.username || localStorage.getItem('posUser') || 'unknown').trim().toLowerCase()}`;
};
const defaults = (): PinLockState => ({ hasPin: false, timeoutSeconds: 180, locked: false, lastActivityAt: Date.now() });
export const readPinLock = (key: string): PinLockState => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return defaults();
    const state = JSON.parse(raw);
    if (typeof state.hasPin !== 'boolean' || typeof state.locked !== 'boolean'
      || !Number.isFinite(state.lastActivityAt) || !Number.isFinite(state.timeoutSeconds)) throw new Error('Invalid lock state');
    const timeoutSeconds = Math.max(30, Math.min(3600, state.timeoutSeconds));
    return { ...state, timeoutSeconds, settingsGeneration: Number.isSafeInteger(state.settingsGeneration) && state.settingsGeneration >= 0 ? state.settingsGeneration : 0, locked: state.locked || (state.hasPin && Date.now() - state.lastActivityAt >= timeoutSeconds * 1000) };
  } catch {
    return { ...defaults(), hasPin: true, locked: true }; // A corrupt record must not unlock the screen.
  }
};
export const writePinLock = (key: string, state: PinLockState) => {
  localStorage.setItem(key, JSON.stringify(state));
  window.dispatchEvent(new CustomEvent(PIN_LOCK_CHANGED, { detail: key }));
};
export const canUsePinLockCache = (key: string) => {
  const state = readPinLock(key);
  // An already locked screen can show PIN entry immediately; verification still goes to the server.
  if (state.hasPin && state.locked) return true;
  const age = Date.now() - Number(state.settingsCheckedAt);
  return Number.isFinite(age) && age >= 0 && age < 5 * 60 * 1000;
};
export const configurePinLock = (key: string, settings: PinSettings, options: { expectedGeneration?: number } = {}) => {
  const current = readPinLock(key);
  const generation = current.settingsGeneration || 0;
  const currentVersion = Date.parse(current.settingsUpdatedAt || '');
  const incomingVersion = Date.parse(settings.updatedAt || '');
  // A settings save/verification in any local tab invalidates older in-flight reads.
  if (options.expectedGeneration !== undefined && options.expectedGeneration !== generation) return current;
  if (Number.isFinite(currentVersion) && (!Number.isFinite(incomingVersion) || incomingVersion < currentVersion)) return current;
  const timeoutSeconds = Math.max(30, Math.min(3600, Number(settings.timeoutSeconds) || 180));
  const next = {
    ...current, hasPin: settings.hasPin, timeoutSeconds,
    settingsCheckedAt: Date.now(), settingsUpdatedAt: settings.updatedAt || null,
    settingsGeneration: generation + Number(current.hasPin !== settings.hasPin || current.timeoutSeconds !== timeoutSeconds || current.settingsUpdatedAt !== (settings.updatedAt || null)),
  };
  // A successful settings fetch is not authentication and must not clear an existing lock.
  next.locked = current.locked || (next.hasPin && Date.now() - next.lastActivityAt >= next.timeoutSeconds * 1000);
  writePinLock(key, next);
  return next;
};
export const unlockPinAfterVerification = (key = getPinLockKey()) => {
  writePinLock(key, { ...readPinLock(key), locked: false, lastActivityAt: Date.now() });
};
export const touchPinLock = (key: string) => {
  const state = readPinLock(key);
  if (state.locked) return state;
  const next = { ...state, lastActivityAt: Date.now() };
  writePinLock(key, next);
  return next;
};
